import { onMount, onCleanup } from 'solid-js'

// 粒子格數：桌機固定 20×20；手機依容器尺寸每 MOBILE_CELL px 一格，否則 400 個點擠在小螢幕連線糊成一片
const MOBILE_BP = 768
const MOBILE_CELL = 80
const DESKTOP_GRID = 20
function gridFor(width, height) {
  if (width >= MOBILE_BP) return { cols: DESKTOP_GRID, rows: DESKTOP_GRID }
  return { cols: Math.max(4, Math.round(width / MOBILE_CELL)), rows: Math.max(6, Math.round(height / MOBILE_CELL)) }
}

/**
 * 粒子連線動畫 Canvas 元件
 * 基於 CodePen by MarcoGuglielmelli，移除 GSAP 依賴改用純 JS 動畫
 */
export default function ParticleCanvas() {
  let canvasRef
  let animationId
  let points = []
  let target = { x: 0, y: 0 }
  let animateHeader = true

  function getDistance(p1, p2) {
    return Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2)
  }

  // 用純 JS 取代 TweenLite 的緩動動畫
  function shiftPoint(p) {
    const duration = 1000 + Math.random() * 1000
    const startX = p.x
    const startY = p.y
    const endX = p.originX - 50 + Math.random() * 100
    const endY = p.originY - 50 + Math.random() * 100
    const startTime = performance.now()

    function easeInOut(t) {
      return t < 0.5
        ? 0.5 * (1 - Math.sqrt(1 - 4 * t * t))
        : 0.5 * (Math.sqrt(1 - Math.pow(-2 * t + 2, 2)) + 1)
    }

    function tick(now) {
      const elapsed = now - startTime
      const progress = Math.min(elapsed / duration, 1)
      const ease = easeInOut(progress)
      p.x = startX + (endX - startX) * ease
      p.y = startY + (endY - startY) * ease
      if (progress < 1) {
        requestAnimationFrame(tick)
      } else {
        shiftPoint(p)
      }
    }
    requestAnimationFrame(tick)
  }

  function init() {
    if (!canvasRef || !canvasRef.parentElement) {
      return
    }
    const container = canvasRef.parentElement
    const width = container.offsetWidth
    const height = container.offsetHeight
    target = { x: width / 2, y: height / 2 }

    canvasRef.width = width
    canvasRef.height = height
    const ctx = canvasRef.getContext('2d')

    // 建立粒子點（格數見 gridFor）
    points = []
    const { cols, rows } = gridFor(width, height)
    const cellW = width / cols
    const cellH = height / rows
    for (let x = 0; x < width; x += cellW) {
      for (let y = 0; y < height; y += cellH) {
        const px = x + Math.random() * cellW
        const py = y + Math.random() * cellH
        points.push({ x: px, originX: px, y: py, originY: py })
      }
    }

    // 找出每個點最近的 5 個鄰居
    for (let i = 0; i < points.length; i++) {
      const closest = []
      const p1 = points[i]
      for (let j = 0; j < points.length; j++) {
        const p2 = points[j]
        if (p1 === p2) continue
        let placed = false
        for (let k = 0; k < 5; k++) {
          if (!placed && closest[k] === undefined) {
            closest[k] = p2
            placed = true
          }
        }
        for (let k = 0; k < 5; k++) {
          if (!placed && getDistance(p1, p2) < getDistance(p1, closest[k])) {
            closest[k] = p2
            placed = true
          }
        }
      }
      p1.closest = closest
    }

    // 每個點附加一個圓
    for (const p of points) {
      p.circle = { radius: 2 + Math.random() * 2, active: 0 }
    }

    // 啟動緩動動畫
    for (const p of points) {
      shiftPoint(p)
    }

    // 主繪製迴圈
    function animate() {
      if (animateHeader) {
        ctx.clearRect(0, 0, width, height)
        for (const p of points) {
          const dist = getDistance(target, p)
          if (dist < 4000) {
            p.active = 0.3
            p.circle.active = 0.6
          } else if (dist < 20000) {
            p.active = 0.1
            p.circle.active = 0.3
          } else if (dist < 40000) {
            p.active = 0.02
            p.circle.active = 0.1
          } else {
            p.active = 0
            p.circle.active = 0
          }

          // 畫連線
          if (p.active) {
            for (const c of p.closest) {
              ctx.beginPath()
              ctx.moveTo(p.x, p.y)
              ctx.lineTo(c.x, c.y)
              ctx.strokeStyle = `rgba(156,217,249,${p.active})`
              ctx.stroke()
            }
          }

          // 畫圓點
          if (p.circle.active) {
            ctx.beginPath()
            ctx.arc(p.x, p.y, p.circle.radius, 0, 2 * Math.PI, false)
            ctx.fillStyle = `rgba(156,217,249,${p.circle.active})`
            ctx.fill()
          }
        }
      }
      animationId = requestAnimationFrame(animate)
    }
    animate()
  }

  function onMouseMove(e) {
    const rect = canvasRef.getBoundingClientRect()
    target.x = e.clientX - rect.left
    target.y = e.clientY - rect.top
  }

  // 手機沒有滑鼠：拿裝置傾斜角當游標。gamma 左右傾 ±45° 掃滿寬度、beta 以手持約 45° 為中心前後傾 ±45° 掃滿高度。
  // iOS 13+ 要在使用者手勢內呼叫 DeviceOrientationEvent.requestPermission() 才會發事件，這裡不主動跳系統權限框；
  // 使用者若已在 Safari 設定開放動作與方向存取則直接生效，Android Chrome 不需授權。
  function onOrientation(e) {
    if (e.gamma == null || e.beta == null || !canvasRef) return
    const clamp = (v) => Math.max(-1, Math.min(1, v))
    target.x = (canvasRef.width / 2) * (1 + clamp(e.gamma / 45))
    target.y = (canvasRef.height / 2) * (1 + clamp((e.beta - 45) / 45))
  }

  function onResize() {
    cancelAnimationFrame(animationId)
    init()
  }

  onMount(() => {
    init()
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('deviceorientation', onOrientation)
    window.addEventListener('resize', onResize)
  })

  onCleanup(() => {
    cancelAnimationFrame(animationId)
    window.removeEventListener('mousemove', onMouseMove)
    window.removeEventListener('deviceorientation', onOrientation)
    window.removeEventListener('resize', onResize)
  })

  return <canvas ref={canvasRef} class="absolute inset-0 w-full h-full" style={{ 'z-index': 1 }} />
}
